"use client";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import styles from "./ErrorFallback.module.scss";
import { Box, Button, Typography } from "@mui/material";
import Link from "next/link";
import { useContext } from "react";
import { ErrorBoundaryContext } from "react-error-boundary";

interface Props {
    isErrorPage?: boolean;
}

const ErrorFallback = ({ isErrorPage }: Props) => {
    // Rendered as an ErrorBoundary fallback and also directly by pages/_error.tsx, where there is
    // no boundary: useErrorBoundary() throws there, which turned every uncaught page error into
    // Next's generic error screen (#1286). Read the context and tolerate its absence instead.
    const resetBoundary = useContext(ErrorBoundaryContext)?.resetErrorBoundary;

    return (
        <div className={styles.fallback}>
            <Box className={styles.container}>
                <ErrorOutlineIcon style={{ fontSize: 150 }} />
                <Typography variant="h4" sx={{ mt: 4 }}>
                    Something went wrong
                </Typography>
                <Typography variant="body1">Please refresh the page or try again later</Typography>
                <Box className={styles.actions}>
                    {!isErrorPage && resetBoundary && (
                        <Button variant="outlined" sx={{ mt: 2 }} onClick={resetBoundary}>
                            Try again
                        </Button>
                    )}
                    <Button variant="outlined" sx={{ mt: 2, ml: !isErrorPage && 1 }}>
                        <Link passHref legacyBehavior href="/">
                            <Typography variant="body1" component="span" sx={{ color: "var(--accent-text, #31aba6)", fontSize: "inherit" }}>
                                Go home
                            </Typography>
                        </Link>
                    </Button>
                </Box>
            </Box>
            <Typography variant="body1">
                If the problem persists, please contact us at&nbsp;
                <Link passHref legacyBehavior href="mailto:sales@ortex.com">
                    <Typography variant="body1" component="span" sx={{ color: "var(--accent-text, #31aba6)", cursor: "pointer" }}>
                        support@ortex.com
                    </Typography>
                </Link>
            </Typography>
        </div>
    );
};

export default ErrorFallback;
